/* eslint-disable no-param-reassign */
import produce from 'immer';
import {
  addMessageSuccessHandlers,
  deleteChatMessageSuccessHandlers,
  getMoreChatMessagesSuccessHandlers,
  sendNowOrEditScheduledMessageSuccessHandlers,
  socketChatMessageDeleteHandlers,
  socketChatScheduledMessageDeleteHandlers,
  socketChatNewMessageHandlers,
  socketChatScheduledMessageUpdateHandlers,
  socketChatMessageUpdateHandlers,
  socketChatDeletedHandlers,
} from 'src/handlers/chatHandlers';
import {
  ADD_MESSAGE,
  ADD_MESSAGE_ERROR,
  ADD_MESSAGE_SUCCESS,
  DELETE_CHAT_MESSAGE,
  DELETE_CHAT_MESSAGE_ERROR,
  DELETE_CHAT_MESSAGE_SUCCESS,
  GET_MORE_CHAT_MESSAGES,
  GET_MORE_CHAT_MESSAGES_ERROR,
  GET_MORE_CHAT_MESSAGES_SUCCESS,
  GET_MORE_SCHEDULED_CHAT_MESSAGES,
  GET_MORE_SCHEDULED_CHAT_MESSAGES_SUCCESS,
  GET_MORE_SCHEDULED_CHAT_MESSAGES_ERROR,
  GET_CHATS_LIST,
  GET_CHATS_LIST_ERROR,
  GET_CHATS_LIST_SUCCESS,
  GET_MORE_CHATS,
  GET_MORE_CHATS_SUCCESS,
  GET_MORE_CHATS_ERROR,
  RESET_CHATS_LIST,
  GET_CONTACTS,
  GET_CONTACTS_ERROR,
  GET_CONTACTS_SUCCESS,
  GET_FILTERED_CHAT_MESSAGES,
  GET_FILTERED_CHAT_MESSAGES_ERROR,
  GET_FILTERED_CHAT_MESSAGES_SUCCESS,
  CLEAR_FILTERED_CHAT_MESSAGES,
  MARK_CHAT_AS_SEEN_SUCCESS,
  MARK_CHAT_AS_UNREAD_SUCCESS,
  SEND_NOW_OR_EDIT_SCHEDULED_MESSAGE,
  SEND_NOW_OR_EDIT_SCHEDULED_MESSAGE_ERROR,
  SEND_NOW_OR_EDIT_SCHEDULED_MESSAGE_SUCCESS,
  SET_CONTACTS_QUERY,
  SET_CURRENT_MESSAGE,
  SET_FILE_UPLOAD_TO_MESSAGE,
  SOCKET_CHAT_TOTAL_UNREAD_COUNT,
  SOCKET_CHAT_NEW_MESSAGE,
  SOCKET_CHAT_NEW_SCHEDULED_MESSAGE,
  SOCKET_CHAT_MESSAGE_DELETE,
  SOCKET_CHAT_SCHEDULED_MESSAGE_DELETE,
  SOCKET_CHAT_SCHEDULED_MESSAGE_UPDATE,
  SOCKET_CHAT_MESSAGE_SENT,
  SOCKET_CHAT_SCHEDULED_MESSAGE_SENT,
  SOCKET_CHAT_MESSAGE_READ,
  SOCKET_CHAT_MESSAGE_DELIVERED,
  SOCKET_CHAT_DELETED,
  CLEAR_DELETED_CHAT_KEY,
  GET_FOLLOW_UP_MESSAGES,
  GET_FOLLOW_UP_MESSAGES_SUCCESS,
  GET_FOLLOW_UP_MESSAGES_ERROR,
  REPLACE_SYSTEM_GEN_MESSAGE,
  REPLACE_SYSTEM_GEN_MESSAGE_SUCCESS,
  REPLACE_SYSTEM_GEN_MESSAGE_ERROR,
  UPDATE_CHAT_PREFERENCES_SUCCESS,
  SAVE_DRAFT_SUCCESS,
  DELETE_DRAFT_SUCCESS,
  SOCKET_CHAT_AI_DRAFT_READY,
} from 'src/actions/chatActions';
import {
  GET_CUSTOMER_SUCCESS,
} from 'src/actions/customerDetailsActions';
import objFromArray from 'src/utils/objFromArray';
import { buildChatCursor } from 'src/helpers/chatCursor';

const followUpMessages = {
  followUpMessageList: [],
  loading: false,
};

const currentMessage = {
  systemGenMessageReplaced: false,
  systemGenEditMessageReplaced: false,
  loading: false,
};

const initialState = {
  isChatsLoading: false,
  // CON-1673. Bumped whenever an AI draft or research note lands over the
  // socket. The compose box watches the version, not the contents, so a second
  // draft for the same contact is still a distinct signal.
  aiDraftSignal: { contactId: null, kind: null, version: 0 },
  isMessagesLoading: false,
  isMessageSending: false,
  isMessageUpdating: false,
  chatsContacts: {
    byId: {},
    allIds: []
  },
  chats: {
    byKey: {},
    allKeys: []
  },
  filteredChats: {
    byKey: {},
    allKeys: []
  },
  allContactsList: {
    isContactsLoading: false,
    contacts: [],
    query: {
      search: '',
      sort: [['firstName', 'asc'], ['lastName', 'asc']],
      page: 1,
      pageSize: 50,
      count: 0,
    },
  },
  deletedChatKey: '',
  userCanDeleteTexts: false,
  totalUnreadCount: 0,
  currentMessage,
  messagesFilteredByPhone: '',
  followUpMessages,
  // ===== PROPERTIES FOR LAZY LOADING =====
  isChatsLoadingMore: false,
  // CON-1746 search hardening: generation of the list request currently
  // wanted on screen. Responses tagged with an older generation are dropped.
  chatsListGeneration: 0,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalChats: 0,
    limit: 50,
    hasMore: false,
  },
};

// CON-1746: a list response is stale when it carries a generation older than
// the one the reducer last started. Untagged actions (older callers, tests)
// are never dropped.
const isStaleChatsListAction = (state, action) => {
  const generation = action?.meta?.generation;
  return generation !== undefined && generation !== state.chatsListGeneration;
};

const chatReducer = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case GET_CHATS_LIST: {
      return produce(state, (draft) => {
        draft.isChatsLoading = true;
        if (action?.meta?.generation !== undefined) {
          draft.chatsListGeneration = action.meta.generation;
        }
      });
    }

    case GET_CHATS_LIST_SUCCESS: {
      if (isStaleChatsListAction(state, action)) {
        return state;
      }
      const {
        chats,
        contacts,
        canDeleteTexts,
        pagination,
      } = payload;

      // let totalUnreadCount = 0; // We shouldn't calculate this value, as we can get it from the response.
      const chatsWithNextCursor = {
        byKey: chats?.byKey || {},
        allKeys: chats?.allKeys || [],
      };

      chatsWithNextCursor.allKeys.forEach((key) => {
        const chat = chatsWithNextCursor.byKey[key];
        // totalUnreadCount += chat?.unreadCount || 0;
        if (chat?.messages?.length) {
          // The oldest loaded row's keyset cursor, in the server's own format
          // (CON-1805), so "load older" continues exactly where this page ends.
          chat.nextCursor = buildChatCursor(chat.messages[0]);
        }

        if (chat?.scheduledMessages?.length) {
          chat.scheduledNextCursor = chat.scheduledMessages.at(-1).scheduledAt;
        }
      });

      const chatsContacts = {
        byId: contacts?.byId || {},
        allIds: contacts?.allIds || [],
      };

      return produce(state, (draft) => {
        draft.chats = chatsWithNextCursor;
        draft.chatsContacts = chatsContacts;
        draft.userCanDeleteTexts = canDeleteTexts || false;
        draft.totalUnreadCount = chats?.totalUnreadCount || 0;
        draft.isChatsLoading = false;
        draft.pagination = pagination || state.pagination;
      });
    }

    case GET_CHATS_LIST_ERROR: {
      if (isStaleChatsListAction(state, action)) {
        return state;
      }
      return produce(state, (draft) => {
        draft.isChatsLoading = false;
      });
    }

    case GET_CONTACTS: {
      return produce(state, (draft) => {
        draft.allContactsList.isContactsLoading = true;
      });
    }

    case GET_CONTACTS_SUCCESS: {
      const { isLoadMore, data } = payload;
      return produce(state, (draft) => {
        if (isLoadMore) {
          draft.allContactsList.contacts.push(...(data?.contacts || []));
        } else {
          draft.allContactsList.contacts = data?.contacts || [];
        }
        draft.allContactsList.query.count = data?.count || 0;
        draft.allContactsList.isContactsLoading = false;
      });
    }

    case GET_CONTACTS_ERROR: {
      return produce(state, (draft) => {
        draft.allContactsList.isContactsLoading = false;
      });
    }

    case SET_CONTACTS_QUERY: {
      return produce(state, (draft) => {
        draft.allContactsList.query = payload ? {
          ...state.allContactsList.query,
          ...payload
        } : initialState.allContactsList.query;
      });
    }

    case GET_MORE_CHAT_MESSAGES: {
      return produce(state, (draft) => {
        draft.isMessagesLoading = true;
      });
    }

    case GET_MORE_CHAT_MESSAGES_SUCCESS: {
      return getMoreChatMessagesSuccessHandlers(state, payload);
    }

    case GET_MORE_CHAT_MESSAGES_ERROR: {
      return produce(state, (draft) => {
        draft.isMessagesLoading = false;
      });
    }

    case GET_MORE_SCHEDULED_CHAT_MESSAGES: {
      return produce(state, (draft) => {
        draft.isMessagesLoading = true;
      });
    }

    case GET_MORE_SCHEDULED_CHAT_MESSAGES_SUCCESS: {
      const { contactId, data } = payload;
      const { messages = [], nextCursor = null } = data;

      return produce(state, (draft) => {
        const chatKey = String(contactId);

        // Use appropriate chat store based on filter state
        const chatStore = draft.messagesFilteredByPhone ? draft.filteredChats : draft.chats;
        const chat = chatStore.byKey[chatKey];

        // If chat doesn't exist, warn and exit gracefully
        if (!chat) {
          console.warn('[chatReducer] Scheduled messages loaded before regular messages for:', chatKey);
          draft.isMessagesLoading = false;
          return;
        }

        // Add scheduled messages (with duplicate check)
        messages.forEach((message) => {
          if (!chat.scheduledMessages.find((item) => item?.id && item.id === message?.id)) {
            chat.scheduledMessages.push(message);
          }
        });
        chat.scheduledNextCursor = nextCursor;

        draft.isMessagesLoading = false;
      });
    }

    case GET_MORE_SCHEDULED_CHAT_MESSAGES_ERROR: {
      return produce(state, (draft) => {
        draft.isMessagesLoading = false;
      });
    }

    case GET_FILTERED_CHAT_MESSAGES: {
      return produce(state, (draft) => {
        draft.isMessagesLoading = true;
      });
    }

    case GET_FILTERED_CHAT_MESSAGES_SUCCESS: {
      const {
        chatKey,
        filteredByPhone,
        regularMessages,
        scheduledMessages
      } = payload;

      return produce(state, (draft) => {
        if (!draft.filteredChats.byKey[chatKey]) {
          draft.filteredChats.byKey[chatKey] = {
            key: chatKey,
            messages: [],
            nextCursor: null,
            scheduledMessages: [],
            scheduledNextCursor: null,
          };
        }
        draft.filteredChats.byKey[chatKey].messages = regularMessages.messages;
        draft.filteredChats.byKey[chatKey].nextCursor = regularMessages.nextCursor;
        draft.filteredChats.byKey[chatKey].scheduledMessages = scheduledMessages.messages;
        draft.filteredChats.byKey[chatKey].scheduledNextCursor = scheduledMessages.nextCursor;
        draft.messagesFilteredByPhone = filteredByPhone;
        draft.isMessagesLoading = false;
      });
    }

    case GET_FILTERED_CHAT_MESSAGES_ERROR: {
      return produce(state, (draft) => {
        draft.isMessagesLoading = false;
      });
    }

    case CLEAR_FILTERED_CHAT_MESSAGES: {
      return produce(state, (draft) => {
        draft.filteredChats = initialState.filteredChats;
        draft.messagesFilteredByPhone = initialState.messagesFilteredByPhone;
      });
    }

    case MARK_CHAT_AS_SEEN_SUCCESS: {
      const { chatKey, updatedMessages } = payload;
      const updates = objFromArray(updatedMessages);

      return produce(state, (draft) => {
        const chat = draft.chats.byKey?.[chatKey];
        if (!chat?.messages) return;

        chat.unreadCount = Math.max(chat.unreadCount - updatedMessages.length, 0);
        chat.messages = chat.messages.map((message) => (
          updates[message.id] ? { ...message, ...updates[message.id] } : message
        ));
      });
    }

    case MARK_CHAT_AS_UNREAD_SUCCESS: {
      const { chatKey, updatedMessages } = payload;
      const updates = objFromArray(updatedMessages);

      return produce(state, (draft) => {
        const chat = draft.chats.byKey?.[chatKey];
        if (!chat?.messages) return;

        chat.unreadCount += updatedMessages.length;
        chat.messages = chat.messages.map((message) => (
          updates[message.id] ? { ...message, ...updates[message.id] } : message
        ));
      });
    }

    case ADD_MESSAGE: {
      return produce(state, (draft) => {
        draft.isMessageSending = true;
      });
    }

    case ADD_MESSAGE_SUCCESS: {
      return addMessageSuccessHandlers(state, payload);
    }

    case ADD_MESSAGE_ERROR: {
      return produce(state, (draft) => {
        draft.isMessageSending = false;
      });
    }

    case SOCKET_CHAT_TOTAL_UNREAD_COUNT: {
      const {
        totalUnreadCount,
      } = payload || {};

      return produce(state, (draft) => {
        draft.totalUnreadCount = totalUnreadCount || 0;
      });
    }

    case SOCKET_CHAT_NEW_MESSAGE:
    case SOCKET_CHAT_NEW_SCHEDULED_MESSAGE: {
      return socketChatNewMessageHandlers(state, payload);
    }

    case SEND_NOW_OR_EDIT_SCHEDULED_MESSAGE: {
      return produce(state, (draft) => {
        draft.isMessageSending = true;
      });
    }

    case SEND_NOW_OR_EDIT_SCHEDULED_MESSAGE_SUCCESS: {
      return sendNowOrEditScheduledMessageSuccessHandlers(state, payload);
    }

    case SEND_NOW_OR_EDIT_SCHEDULED_MESSAGE_ERROR: {
      return produce(state, (draft) => {
        draft.isMessageSending = false;
      });
    }

    case DELETE_CHAT_MESSAGE: {
      return produce(state, (draft) => {
        draft.isMessageUpdating = true;
      });
    }

    case DELETE_CHAT_MESSAGE_SUCCESS: {
      return deleteChatMessageSuccessHandlers(state, payload);
    }

    case DELETE_CHAT_MESSAGE_ERROR: {
      return produce(state, (draft) => {
        draft.isMessageUpdating = false;
      });
    }

    case SOCKET_CHAT_MESSAGE_DELETE: {
      return socketChatMessageDeleteHandlers(state, payload);
    }

    case SOCKET_CHAT_SCHEDULED_MESSAGE_DELETE: {
      return socketChatScheduledMessageDeleteHandlers(state, payload);
    }

    case SOCKET_CHAT_SCHEDULED_MESSAGE_UPDATE:
    case SOCKET_CHAT_SCHEDULED_MESSAGE_SENT: {
      return socketChatScheduledMessageUpdateHandlers(state, payload);
    }

    case SOCKET_CHAT_MESSAGE_SENT:
    case SOCKET_CHAT_MESSAGE_DELIVERED: {
      return socketChatMessageUpdateHandlers(state, payload);
    }

    case SOCKET_CHAT_MESSAGE_READ: {
      const newPayload = {
        ...payload,
        isMessageReadStatusChanged: true,
      };

      return socketChatMessageUpdateHandlers(state, newPayload);
    }

    case SOCKET_CHAT_DELETED: {
      return socketChatDeletedHandlers(state, payload);
    }

    case CLEAR_DELETED_CHAT_KEY: {
      return produce(state, (draft) => {
        draft.deletedChatKey = '';
      });
    }

    case SET_CURRENT_MESSAGE: {
      return produce(state, (draft) => {
        draft.currentMessage = payload;
      });
    }

    case SET_FILE_UPLOAD_TO_MESSAGE: {
      const { isMessageEdit, file } = payload;
      return produce(state, (draft) => {
        draft.currentMessage = {
          ...state.currentMessage,
          [isMessageEdit ? 'fileToReplace' : 'fileUpload']: file,
        };
      });
    }

    case GET_FOLLOW_UP_MESSAGES: {
      return produce(state, (draft) => {
        draft.followUpMessages.loading = true;
      });
    }

    case GET_FOLLOW_UP_MESSAGES_SUCCESS:
    case GET_FOLLOW_UP_MESSAGES_ERROR: {
      return produce(state, (draft) => {
        draft.followUpMessages.loading = false;
        draft.followUpMessages.followUpMessageList = payload ? [
          ...state.followUpMessages.followUpMessageList,
          ...payload,
        ] : followUpMessages.followUpMessageList;
      });
    }

    case REPLACE_SYSTEM_GEN_MESSAGE: {
      return produce(state, (draft) => {
        draft.isMessageUpdating = true;
      });
    }

    case REPLACE_SYSTEM_GEN_MESSAGE_SUCCESS:
    case REPLACE_SYSTEM_GEN_MESSAGE_ERROR: {
      const { isMessageEdit, messageWithReplacedFields } = payload || {};
      return produce(state, (draft) => {
        draft.isMessageUpdating = false;
        if (isMessageEdit) {
          draft.currentMessage.systemGenEditMessageReplaced = messageWithReplacedFields || false;
        } else {
          draft.currentMessage.systemGenMessageReplaced = messageWithReplacedFields || false;
        }
      });
    }

    case GET_MORE_CHATS: {
      return produce(state, (draft) => {
        draft.isChatsLoadingMore = true;
      });
    }

    case GET_MORE_CHATS_SUCCESS:
      if (isStaleChatsListAction(state, action)) {
        return state;
      }
      return produce(state, (draft) => {
        const { contacts, chats, pagination } = action.payload;

        // Merge new contacts (avoid duplicates)
        if (contacts?.byId) {
          Object.assign(draft.chatsContacts.byId, contacts.byId);
        }
        if (contacts?.allIds) {
          const newContactIds = contacts.allIds.filter(
            (id) => !draft.chatsContacts.allIds.includes(id)
          );
          draft.chatsContacts.allIds.push(...newContactIds);
        }

        // Merge new chats (avoid duplicates)
        if (chats?.byKey) {
          Object.assign(draft.chats.byKey, chats.byKey);
        }
        if (chats?.allKeys) {
          const newChatKeys = chats.allKeys.filter(
            (key) => !draft.chats.allKeys.includes(key)
          );
          draft.chats.allKeys.push(...newChatKeys);
        }

        draft.pagination = pagination || state.pagination;

        draft.isChatsLoadingMore = false;
      });

    case GET_MORE_CHATS_ERROR: {
      if (isStaleChatsListAction(state, action)) {
        return state;
      }
      return produce(state, (draft) => {
        draft.isChatsLoadingMore = false;
      });
    }

    case RESET_CHATS_LIST: {
      return produce(state, (draft) => {
        draft.chatsContacts = {
          byId: {},
          allIds: [],
        };
        draft.chats = {
          byKey: {},
          allKeys: [],
        };
        draft.filteredChats = {
          byKey: {},
          allKeys: [],
        };
        draft.totalUnreadCount = 0;
        draft.pagination = {
          currentPage: 1,
          totalPages: 1,
          totalChats: 0,
          limit: 50,
          hasMore: false,
        };
      });
    }

    // We should list this action from another reducer to fix the error if we open a new chat with undefined contact
    case GET_CUSTOMER_SUCCESS: {
      if (
        payload?.contact?.id
        && state?.chatsContacts?.byId?.[payload.contact.id]?.id
        && !state?.chatsContacts?.byId?.[payload.contact.id]?.firstName
      ) {
        return produce(state, (draft) => {
          draft.chatsContacts.byId[payload.contact.id] = {
            ...draft.chatsContacts.byId[payload.contact.id],
            ...payload.contact,
          };
        });
      }

      return state;
    }

    case UPDATE_CHAT_PREFERENCES_SUCCESS:
    case SAVE_DRAFT_SUCCESS:
    case DELETE_DRAFT_SUCCESS: {
      const { contactId, chatPreferences } = payload;

      return produce(state, (draft) => {
        // contactId is already a string from the action
        const contact = draft.chatsContacts.byId?.[contactId];
        if (contact) {
          // Merge new preferences with existing, not replace entirely
          contact.chatPreferences = {
            ...contact.chatPreferences,
            ...chatPreferences,
          };
        }
      });
    }

    case SOCKET_CHAT_AI_DRAFT_READY: {
      const { contactId, kind, chatPreferences } = payload;

      return produce(state, (draft) => {
        const contact = draft.chatsContacts.byId?.[contactId];
        if (contact && chatPreferences) {
          contact.chatPreferences = {
            ...contact.chatPreferences,
            ...chatPreferences,
          };
        }
        // One slot rather than a per-contact map: only one chat is open at a
        // time, and the compose box compares the contactId before acting on
        // it. The counter is what makes a second draft for the same contact
        // distinguishable from the first.
        draft.aiDraftSignal = {
          contactId,
          kind,
          version: (draft.aiDraftSignal?.version || 0) + 1,
        };
      });
    }

    default: {
      return state;
    }
  }
};

export default chatReducer;
